import { useEffect, useState } from "react";
import { CheckCircle2, BookOpen, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";

export const OBJECTIVE_STATUS = { pendente: "Pendente", em_andamento: "Em andamento", concluido: "Concluído" };
const NEXT_STATUS = { pendente: "em_andamento", em_andamento: "concluido" };

const ObjectiveStatusBadge = ({ status }) => (
  <Badge
    variant={status === "concluido" ? "default" : "secondary"}
    className={status === "concluido" ? "bg-primary text-primary-foreground" : ""}
  >
    {OBJECTIVE_STATUS[status] || status}
  </Badge>
);

export function PedagogyPanel({ enrollmentId, testIdPrefix = "pedagogy" }) {
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", order: 1, status: "pendente" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    const { data: d } = await api.get(`/pedagogy/enrollments/${enrollmentId}`);
    setData(d);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentId]);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", description: "", order: (data?.progress.total || 0) + 1, status: "pendente" });
    setFormOpen(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({ title: o.title, description: o.description || "", order: o.order, status: o.status });
    setFormOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/pedagogy/objectives/${editing.id}`, {
          title: form.title,
          description: form.description,
          order: Number(form.order),
          status: form.status,
        });
        toast.success("Objetivo atualizado.");
      } else {
        await api.post("/pedagogy/objectives", {
          enrollment_id: enrollmentId,
          title: form.title,
          description: form.description,
          order: Number(form.order),
        });
        toast.success("Objetivo criado.");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const advance = async (o) => {
    try {
      await api.put(`/pedagogy/objectives/${o.id}`, { status: NEXT_STATUS[o.status] });
      toast.success(`Status: ${OBJECTIVE_STATUS[NEXT_STATUS[o.status]]}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/pedagogy/objectives/${deleting.id}`);
      toast.success("Objetivo excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  const applyCurriculum = async () => {
    setApplying(true);
    try {
      const { data: r } = await api.post(`/pedagogy/enrollments/${enrollmentId}/apply-curriculum`);
      if (r.applied) {
        toast.success(`Currículo "${r.curriculum}": ${r.created} objetivo(s) criado(s), ${r.skipped_existing} já existente(s).`);
        load();
      } else {
        toast.info(r.message);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  if (!data) return null;
  const { objectives, progress } = data;

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge variant="secondary" data-testid={`${testIdPrefix}-progress`}>
          {progress.concluidos}/{progress.total} concluído(s) · {progress.percentual}%
        </Badge>
        <div className="flex items-center gap-2">
          {data.curriculum_available && (
            <Button size="sm" variant="outline" onClick={applyCurriculum} disabled={applying} data-testid={`${testIdPrefix}-apply-curriculum-button`}>
              <BookOpen size={14} className="mr-1" />
              {applying ? "Aplicando..." : "Aplicar currículo do curso"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={openNew} data-testid={`${testIdPrefix}-objective-add-button`}>
            <Plus size={14} className="mr-1" /> Objetivo
          </Button>
        </div>
      </div>
      <div className="h-1.5 w-full bg-zinc-200">
        <div className="h-full bg-primary transition-[width]" style={{ width: `${progress.percentual}%` }} data-testid={`${testIdPrefix}-progress-bar`} />
      </div>

      {objectives.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
          Nenhum objetivo cadastrado nesta matrícula.
        </p>
      )}
      <div className="space-y-2">
        {objectives.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center gap-2 border border-border p-2" data-testid={`objective-row-${o.id}`}>
            <span className="text-xs font-bold text-muted-foreground">#{o.order}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{o.title}</p>
              {o.description && <p className="text-xs text-muted-foreground">{o.description}</p>}
            </div>
            <ObjectiveStatusBadge status={o.status} />
            {NEXT_STATUS[o.status] && (
              <Button
                variant="ghost"
                size="icon"
                title={`Marcar como ${OBJECTIVE_STATUS[NEXT_STATUS[o.status]]}`}
                onClick={() => advance(o)}
                data-testid={`objective-advance-${o.id}`}
              >
                <CheckCircle2 size={16} />
              </Button>
            )}
            <Button variant="ghost" size="icon" title="Editar" onClick={() => openEdit(o)} data-testid={`objective-edit-${o.id}`}>
              <Pencil size={16} />
            </Button>
            {o.status === "pendente" && (
              <Button variant="ghost" size="icon" title="Excluir" onClick={() => setDeleting(o)} data-testid={`objective-delete-${o.id}`}>
                <Trash2 size={16} className="text-destructive" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {formOpen && (
        <form onSubmit={save} className="space-y-3 border border-border bg-zinc-50 p-3" data-testid={`${testIdPrefix}-objective-form`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {editing ? "Editar objetivo" : "Novo objetivo"}
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_110px]">
            <div className="space-y-1">
              <Label className="text-xs">Título *</Label>
              <Input required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={`${testIdPrefix}-objective-title-input`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ordem</Label>
              <Input type="number" min="0" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid={`${testIdPrefix}-objective-order-input`} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Descrição</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`${testIdPrefix}-objective-description-input`} />
          </div>
          {editing && (
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid={`${testIdPrefix}-objective-status-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(OBJECTIVE_STATUS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid={`${testIdPrefix}-objective-save-button`}>
              {saving ? "Salvando..." : editing ? "Salvar alterações" : "Criar objetivo"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setFormOpen(false)} data-testid={`${testIdPrefix}-objective-cancel-button`}>
              Cancelar
            </Button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Excluir objetivo"
        description={`Excluir "${deleting?.title}"? Somente objetivos pendentes podem ser excluídos.`}
        onConfirm={remove}
        testId={`${testIdPrefix}-objective-delete`}
      />
    </div>
  );
}
